import { AlertTriangle, CheckCircle2, Eye, Pencil, Plus, User, Users } from 'lucide-react';
import { getCertMicroBadges, getCertStatus, MODAL_CERT_OPTIONS } from './mockDriverErpData';

function countCertAlerts(driver) {
  let critical = 0;
  MODAL_CERT_OPTIONS.forEach(({ key }) => {
    const s = getCertStatus(driver.certifications?.[key]);
    if (s.tier === 'critical') critical += 1;
  });
  return { critical };
}

function CertMicroBadge({ shortLabel, dateStr }) {
  const { tier } = getCertStatus(dateStr);
  const colorClass =
    tier === 'critical'
      ? 'bg-red-100 text-red-800 ring-red-200'
      : tier === 'warning'
        ? 'bg-amber-100 text-amber-800 ring-amber-200'
        : 'bg-violet-100 text-violet-800 ring-violet-200';

  return (
    <span
      className={`inline-flex items-center rounded px-1 py-0.5 font-mono text-[8px] font-bold uppercase tracking-wide ring-1 ${colorClass}`}
      title={dateStr ? `Exp ${dateStr}` : undefined}
    >
      {shortLabel}
    </span>
  );
}

export default function DriverProfilesTab({
  drivers,
  onAddDriver,
  onEditDriver,
  onViewDossier,
  savedMsg,
}) {
  const totalCritical = drivers.reduce((n, d) => n + countCertAlerts(d).critical, 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-3">
          <div className="flex items-center gap-2 rounded-lg border border-logi-grid bg-white px-3 py-2 text-xs text-logi-muted">
            <Users className="h-4 w-4 text-logi-accent" />
            {drivers.length} registered drivers
          </div>
          {totalCritical > 0 && (
            <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs">
              <AlertTriangle className="h-4 w-4 animate-pulse text-red-700" />
              <span className="font-semibold text-red-700">{totalCritical} cert alerts</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          {savedMsg && (
            <span className="flex items-center gap-1.5 text-xs text-emerald-700">
              <CheckCircle2 className="h-3.5 w-3.5" />
              {savedMsg}
            </span>
          )}
          <button
            type="button"
            onClick={onAddDriver}
            className="inline-flex items-center gap-2 rounded-lg border border-[#93C5FD] bg-[#BFDBFE] px-4 py-2.5 text-sm font-bold text-black transition hover:bg-[#93C5FD]"
          >
            <Plus className="h-4 w-4" />
            Add New Driver
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-logi-grid bg-white">
        <table className="w-full table-fixed text-left text-sm">
          <colgroup>
            <col className="w-[28%]" />
            <col className="w-[16%]" />
            <col className="w-[22%]" />
            <col className="w-[24%]" />
            <col className="w-[10%]" />
          </colgroup>
          <thead>
            <tr className="border-b border-logi-grid bg-logi-silver text-[10px] uppercase tracking-wider text-logi-muted">
              <th className="px-3 py-2.5 font-semibold">Driver</th>
              <th className="px-3 py-2.5 font-semibold">Contact</th>
              <th className="px-3 py-2.5 font-semibold">Identity</th>
              <th className="px-3 py-2.5 font-semibold">Certifications</th>
              <th className="px-3 py-2.5 text-right font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-logi-grid">
            {drivers.map((driver) => {
              const alerts = countCertAlerts(driver);
              const badges = getCertMicroBadges(driver);
              const docCount =
                Object.values(driver.identityVault || {}).filter(Boolean).length +
                Object.values(driver.certAttachments || {}).filter(Boolean).length;

              return (
                <tr
                  key={driver.id}
                  onClick={() => onViewDossier(driver)}
                  className="cursor-pointer align-top transition-colors hover:bg-logi-accent/5"
                >
                  <td className="px-3 py-2.5">
                    <div className="flex min-w-0 items-start gap-2.5">
                      {driver.profilePhoto?.previewUrl ? (
                        <img
                          src={driver.profilePhoto.previewUrl}
                          alt=""
                          className="h-9 w-9 shrink-0 rounded-full object-cover ring-2 ring-logi-accent/20"
                        />
                      ) : (
                        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-logi-silver ring-1 ring-logi-grid">
                          <User className="h-4 w-4 text-logi-muted" />
                        </span>
                      )}
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-logi-text">{driver.name}</p>
                        <p className="truncate font-mono text-[10px] text-logi-muted">{driver.id}</p>
                        {alerts.critical > 0 && (
                          <span className="mt-0.5 inline-flex items-center gap-0.5 text-[9px] font-bold text-red-700">
                            <AlertTriangle className="h-2.5 w-2.5 shrink-0" />
                            {alerts.critical} expiring
                          </span>
                        )}
                        {docCount > 0 && (
                          <p className="mt-0.5 text-[9px] text-logi-accent">{docCount} docs vaulted</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    <p className="text-xs font-medium text-logi-text">{driver.nationality}</p>
                    <p className="mt-0.5 break-all font-mono text-[11px] leading-snug text-logi-muted">
                      {driver.mobile}
                    </p>
                  </td>
                  <td className="px-3 py-2.5">
                    <p className="break-all font-mono text-[10px] leading-snug text-logi-muted">
                      <span className="font-semibold text-logi-text">EID </span>
                      {driver.emiratesId}
                    </p>
                    <p className="mt-1 break-all font-mono text-[10px] leading-snug text-logi-muted">
                      <span className="font-semibold text-logi-text">DL </span>
                      {driver.drivingLicenseNumber || '—'}
                    </p>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {badges.length > 0 ? (
                        badges.map(({ shortLabel, key }) => (
                          <CertMicroBadge
                            key={key}
                            shortLabel={shortLabel}
                            dateStr={driver.certifications[key]}
                          />
                        ))
                      ) : (
                        <span className="text-[10px] text-logi-muted">None</span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex flex-col items-end gap-1">
                      <button
                        type="button"
                        onClick={() => onViewDossier(driver)}
                        title="View Dossier"
                        className="inline-flex items-center gap-1 rounded-lg border border-logi-accent/30 bg-logi-accent/10 px-2 py-1 text-[10px] font-semibold text-logi-accent transition hover:bg-logi-accent/15"
                      >
                        <Eye className="h-3 w-3 shrink-0" />
                        <span className="hidden xl:inline">View</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => onEditDriver(driver)}
                        title="Edit Profile"
                        className="inline-flex items-center gap-1 rounded-lg border border-logi-grid bg-logi-silver px-2 py-1 text-[10px] font-semibold text-logi-muted transition hover:border-logi-accent/30 hover:text-logi-accent"
                      >
                        <Pencil className="h-3 w-3 shrink-0" />
                        <span className="hidden xl:inline">Edit</span>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap gap-4 rounded-lg border border-logi-grid bg-white px-4 py-2.5 text-[10px] uppercase tracking-wider text-logi-muted">
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 animate-pulse rounded-full bg-red-400" /> &lt;7 days
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-amber-400" /> &lt;30 days
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-violet-400" /> Valid
        </span>
        <span>· Click any row to open security dossier</span>
      </div>
    </div>
  );
}
